<template>
    <v-container class="fill-height" fluid>
        <SendLinkDialog :is-open="sendLinkDialog" @on-close="closeSendLinkDialog" />

        <v-container class="pa-0">
            <v-row align="center" justify="center">
                <v-col cols="12" sm="8" md="6" lg="5" xl="4">
                    <v-form @submit.prevent="login">
                        <v-card>
                            <v-toolbar flat dark color="secondary">
                                <v-toolbar-title>Logowanie</v-toolbar-title>
                            </v-toolbar>
                            <v-container>
                                <v-row justify="center">
                                    <v-col cols="12">
                                        <v-text-field
                                            v-model="username"
                                            autocomplete="username"
                                            label="Login"
                                            required
                                            prepend-icon="mdi-account"
                                            filled
                                            hide-details
                                        />
                                    </v-col>
                                    <v-col cols="12">
                                        <v-text-field
                                            v-model="password"
                                            name="password"
                                            label="Hasło"
                                            autocomplete="current-password"
                                            required
                                            type="password"
                                            prepend-icon="mdi-lock"
                                            filled
                                            hide-details
                                        />
                                    </v-col>
                                    <v-col cols="auto">
                                        <v-btn
                                            type="submit"
                                            color="success"
                                            size="default"
                                            class="mt-1"
                                            :loading="actionLoading"
                                            :disabled="actionLoading"
                                        >
                                            Zaloguj
                                        </v-btn>
                                    </v-col>
                                </v-row>
                                <v-row justify="center">
                                    <v-col cols="auto" class="pa-0">
                                        <v-btn variant="text" class="button-forgot-password" @click="showSendLinkDialog()">
                                            Nie pamiętasz hasła?
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-card>
                    </v-form>
                </v-col>
            </v-row>
        </v-container>
    </v-container>
</template>

<script setup>
/*
  Organization / ordering applied:
  1. Imports
  2. Props / Emits
  3. Constants
  4. Stores & top-level services
  5. Reactive state (refs/reactive)
  6. Validation rules & setup
  7. Computed
  8. Handlers / methods
  9. Helpers / lifecycle
*/

// 1. Imports
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { computed, defineAsyncComponent, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { errorHandler } from '@/composables/errorHandler'
import { hasPermission } from '@/composables/usePermissionHandler'
import { useRogerCardReader } from '@/composables/useRogerCardReader'
import { useCommonStore, useUserStore } from '@/stores'

// 2. Props / Emits
// none

// 3. Constants
const SendLinkDialog = defineAsyncComponent(() => import('./SendLinkDialog.vue'))

// 4. Stores & top-level services
const commonStore = useCommonStore()
const userStore = useUserStore()
const router = useRouter()
const { t } = useI18n()
useRogerCardReader(loginWithRogerCard)

// 5. Reactive state (refs/reactive)
const username = ref(null)
const password = ref(null)
const actionLoading = ref(false)
const sendLinkDialog = ref(false)

// 6. Validation rules & setup
const rules = computed(() => ({
    username: { required },
    password: { required }
}))

const v$ = useVuelidate(rules, { username, password }, { $scope: 'loginForm' })

// 7. Computed
// none

// 8. Handlers / methods
function showSendLinkDialog() {
    sendLinkDialog.value = true
}

function closeSendLinkDialog() {
    sendLinkDialog.value = false
}

async function login() {
    if (actionLoading.value) {
        return
    }
    actionLoading.value = true

    try {
        const isFormValid = await v$.value.$validate()

        if (!isFormValid) {
            commonStore.showNotification({
                message: t('common.auth.requiredFields'),
                type: 'error'
            })

            return
        }

        // Login
        await userStore.login({
            username: username.value,
            password: password.value
        })

        // Clear form
        username.value = null
        password.value = null

        await completeLogin(() => ({ name: 'dashboard' }))
    } catch (error) {
        errorHandler(error)
    } finally {
        actionLoading.value = false
    }
}

async function loginWithRogerCard(cardNumber) {
    if (actionLoading.value) {
        return
    }
    actionLoading.value = true

    try {
        await userStore.loginWithRogerCard(cardNumber)
        await completeLogin(getRogerCardLoginRoute)
    } catch (error) {
        errorHandler(error)
    } finally {
        actionLoading.value = false
    }
}

// 9. Helpers / lifecycle
async function completeLogin(getRoute) {
    // Get permissions
    await userStore.getUserPermissions()

    // Handle app refresh if needed
    if (commonStore.updated) {
        window.location.reload(true)

        return
    }

    commonStore.toggleMainLoader(false)

    // Navigate
    await router.push(getRoute())
}

function getRogerCardLoginRoute() {
    const reportingRoute = router.resolve({ name: 'reportsV2OperatorReportingHome' })

    return hasPermission(reportingRoute.meta.permission) ? reportingRoute : { name: 'dashboard' }
}
</script>
